import { useEffect, useRef, useState } from 'react';
import httpService from '../../services/httpService';
import { DATA_MANAGEMENT_PAGE_SIZE } from './DataManagementDataTable';

interface PagedData<T> {
  items: T[];
  totalCount: number;
}

interface UseDataManagementPaginationOptions {
  debounceMs?: number;
  errorMessage?: string;
  errorPrefix?: string;
  preserveItemsOnError?: boolean;
  recoverEmptyPageWhenNoResults?: boolean;
}

export const createDataManagementQuery = (filters: Record<string, string | number | undefined>) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && String(value).trim()) params.set(key, String(value).trim());
  });
  return params.toString();
};

const getErrorDetails = (error: unknown) => error instanceof Error ? error.message : 'Unknown error';

export const useDataManagementPagination = <T,>(
  endpoint: string,
  query: string,
  {
    debounceMs = 0,
    errorMessage = '',
    errorPrefix = '',
    preserveItemsOnError = false,
    recoverEmptyPageWhenNoResults = false,
  }: UseDataManagementPaginationOptions = {},
) => {
  const [items, setItems] = useState<T[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const refreshWaiters = useRef<Array<() => void>>([]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    const timer = window.setTimeout(() => {
      const fetchPage = async () => {
        let pageRecoveryNeeded = false;
        try {
          const params = new URLSearchParams(query);
          params.set('page', String(page));
          params.set('pageSize', String(DATA_MANAGEMENT_PAGE_SIZE));
          const response = await httpService.get<PagedData<T>>(`${endpoint}${endpoint.includes("?") ? "&" : "?"}${params.toString()}`);
          if (controller.signal.aborted) return;

          const nextItems = Array.isArray(response.data.items) ? response.data.items : [];
          const nextTotalRecords = response.data.totalCount || 0;
          setItems(nextItems);
          setTotalRecords(nextTotalRecords);
          setError('');
          if (page > 1 && nextItems.length === 0 && (nextTotalRecords > 0 || recoverEmptyPageWhenNoResults)) {
            pageRecoveryNeeded = true;
            setPage(Math.max(1, Math.ceil(nextTotalRecords / DATA_MANAGEMENT_PAGE_SIZE)));
          }
        } catch (errorCause) {
          if (controller.signal.aborted) return;
          if (!preserveItemsOnError) {
            setItems([]);
            setTotalRecords(0);
          }
          setError(errorMessage || (errorPrefix ? `${errorPrefix}: ${getErrorDetails(errorCause)}` : ''));
        } finally {
          if (!controller.signal.aborted) {
            setLoading(false);
            if (!pageRecoveryNeeded)
              refreshWaiters.current.splice(0).forEach(resolve => resolve());
          }
        }
      };

      void fetchPage();
    }, debounceMs);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [endpoint, query, page, refreshKey, debounceMs, errorMessage, errorPrefix, preserveItemsOnError, recoverEmptyPageWhenNoResults]);

  const refresh = () => new Promise<void>(resolve => {
    refreshWaiters.current.push(resolve);
    setRefreshKey(previous => previous + 1);
  });

  return { items, totalRecords, page, setPage, loading, setLoading, error, refresh };
};

export default useDataManagementPagination;
